/**
 * ProfilesListPage — `/admin/business-profiles`
 *
 * Lists all sub-vertical profiles with their currently active version
 * and status. Rows are clickable and drill into the detail page.
 */
import { CaretRight, Warning } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card } from '../../shared/ui/Card';
import { StatusBadge, type StatusVariant } from '../../shared/ui/StatusBadge';
import { cn } from '../../shared/utils/cn';
import { useBusinessProfiles } from '../../features/business-profiles/useBusinessProfiles';
import type { ProfileStatus } from '../../features/business-profiles/types';

const STATUS_VARIANT: Record<ProfileStatus, StatusVariant> = {
  draft: 'neutral',
  shadow: 'info',
  active: 'success',
  deprecated: 'warning',
  archived: 'neutral',
};

export function ProfilesListPage(): JSX.Element {
  const { t } = useTranslation('adminProfiles');
  const profilesQuery = useBusinessProfiles();

  return (
    <div className="px-biz-card-lg py-biz-section max-w-[1200px] mx-auto">
      <header className="mb-biz-section">
        <h1 className="text-biz-3xl biz-heading font-semibold mb-biz-content">
          {t('list.title')}
        </h1>
        <p className="text-biz-base text-biz-muted-foreground">
          {t('list.subtitle')}
        </p>
      </header>

      {profilesQuery.isLoading ? (
        <LoadingSkeleton />
      ) : profilesQuery.isError ? (
        <div
          role="alert"
          className="flex items-start gap-biz-content-lg px-biz-content-lg py-biz-content-lg rounded-biz-card bg-biz-danger-subtle text-biz-danger border border-biz-danger/30"
        >
          <Warning weight="regular" size={20} aria-hidden="true" className="shrink-0" />
          <p className="text-biz-sm">{t('list.loadError')}</p>
        </div>
      ) : (profilesQuery.data?.length ?? 0) === 0 ? (
        <Card className="px-biz-card-lg py-biz-section text-center text-biz-muted-foreground">
          {t('list.empty')}
        </Card>
      ) : (
        <Card className="divide-y divide-biz-border">
          {profilesQuery.data!.map((record) => (
            <Link
              key={record.id}
              to={`/admin/business-profiles/${record.subVerticalKey}`}
              className={cn(
                'flex items-center gap-biz-content-lg',
                'px-biz-card py-biz-content-lg',
                'min-h-[72px] hover:bg-biz-muted',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring focus-visible:ring-inset',
                'transition-colors duration-biz-fast',
              )}
              aria-label={`${record.displayName} — ${record.profileVersion}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-biz-content flex-wrap">
                  <h2 className="text-biz-base font-semibold truncate">
                    {record.displayName}
                  </h2>
                  <span className="text-biz-xs font-mono text-biz-muted-foreground">
                    v{record.profileVersion}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-biz-content flex-wrap text-biz-xs text-biz-muted-foreground">
                  <span className="font-mono">{record.subVerticalKey}</span>
                  <span aria-hidden="true">·</span>
                  <span>
                    {t(`engines.${record.engine}` as const, {
                      defaultValue: record.engine,
                    })}
                  </span>
                  <span aria-hidden="true">·</span>
                  <span>
                    {t(`deliveryModes.${record.deliveryMode}` as const, {
                      defaultValue: record.deliveryMode,
                    })}
                  </span>
                </div>
              </div>

              <StatusBadge
                variant={STATUS_VARIANT[record.status]}
                label={t(`status.${record.status}` as const)}
              />

              <CaretRight
                size={20}
                weight="regular"
                aria-hidden="true"
                className="shrink-0 text-biz-muted-foreground"
              />
            </Link>
          ))}
        </Card>
      )}
    </div>
  );
}

function LoadingSkeleton(): JSX.Element {
  return (
    <Card className="divide-y divide-biz-border" aria-busy="true" aria-live="polite">
      {[0, 1, 2].map((i) => (
        <div key={i} className="px-biz-card py-biz-content-lg">
          <div className="h-6 w-1/3 bg-biz-muted rounded animate-pulse mb-2" />
          <div className="h-4 w-1/5 bg-biz-muted rounded animate-pulse" />
        </div>
      ))}
    </Card>
  );
}
